import { MouseEvent, KeyboardEvent } from 'react';
import {
    trackEvent,
    trackEcommerce,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    EventNameValues,
} from 'dibs-tracking';
import { graphql, readInlineData } from 'react-relay';
import serverVars from 'server-vars';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import { ValueOf } from 'dibs-ts-utils/exports/ValueOf';
import { hasKey } from 'dibs-ts-utils/exports/hasKey';

import type { actionButtonsTracking_item$key } from './__generated__/actionButtonsTracking_item.graphql';

type TrackableEvent = Maybe<KeyboardEvent | MouseEvent>;

type PdpTrackingVpvParams = {
    purchaseStatus?: Maybe<string>;
    priceType?: Maybe<string>;
};

export type PdpTrackingVpvProps = { pdpTrackingVpvParams?: Maybe<PdpTrackingVpvParams> };

type Item = actionButtonsTracking_item$key & PdpTrackingVpvProps;

function isPdpTrackingVpvParams(
    params: unknown,
    key: keyof PdpTrackingVpvParams
): params is PdpTrackingVpvParams {
    return hasKey(params, key);
}

const { EVENT_ADD_CART, EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const {
    INTERACTION_TYPE_MAKE_OFFER,
    INTERACTION_TYPE_ADD_TO_CART,
    INTERACTION_TYPE_ACCEPT_OFFER,
    INTERACTION_TYPE_COUNTER_OFFER,
} = interactionTypeConstants;
const {
    STEP_INTERACTION_MAKE_OFFER,
    STEP_INTERACTION_EMAIL_MAKE_OFFER,
    STEP_INTERACTION_ADD_TO_CART,
    STEP_INTERACTION_ACCEPT_OFFER,
    STEP_INTERACTION_COUNTER_OFFER,
} = stepInteractionConstants;

function getPdpTrackingVpvParamValue(
    params: unknown,
    key: keyof PdpTrackingVpvParams
): Maybe<ValueOf<PdpTrackingVpvParams>> {
    return isPdpTrackingVpvParams(params, key) ? params[key] : null;
}

export const locations = {
    SIMILAR_SOLD: 'SIMILAR_SOLD',
    PRICING_GUIDANCE: 'PRICING_GUIDANCE',
} as const;

export type Location = (typeof locations)[keyof typeof locations];

const purchaseLocation: { [K in Location]: string } = {
    [locations.SIMILAR_SOLD]: 'similar sold item purchase button',
    [locations.PRICING_GUIDANCE]: '',
};

const makeAnOfferLocation: { [K in Location]: string } = {
    [locations.SIMILAR_SOLD]: 'similar sold item make offer button',
    [locations.PRICING_GUIDANCE]: '',
};

function getPurchaseStatus(item?: Maybe<PdpTrackingVpvProps>): Maybe<string> {
    return getPdpTrackingVpvParamValue(item?.pdpTrackingVpvParams, 'purchaseStatus');
}

const itemFragment = graphql`
    fragment actionButtonsTracking_item on Item
    @argumentDefinitions(
        fetchAuction: { type: "Boolean!" }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: pdp }
    )
    @inline {
        serviceId
        contemporaryTrackingString
        title
        seller {
            serviceId
        }
        pricing {
            totalAmount {
                convertedAmounts {
                    USD
                }
            }
        }
        categoryCode
        browseUrl
        isAuctionEnabled
        relevantAuctionSolution(page: $pageDisplayEnum) @include(if: $fetchAuction) {
            bids {
                totalResults
            }
            highestBidAmount {
                convertedAmounts {
                    USD
                }
            }
        }
        relevantAuctionLot(page: $pageDisplayEnum) @include(if: $fetchAuction) {
            buyItNowPrice {
                convertedAmounts {
                    USD
                }
            }
        }
        lastTransaction(buyerId: $userId) @include(if: $fetchAuction) {
            secondChanceOffer {
                amountPerItem {
                    convertedAmounts {
                        USD
                    }
                }
            }
        }
    }
`;

export function fireTrackEcommerce(
    itemRef: Maybe<Item> = null,
    eventName: EventNameValues | undefined = undefined
): void {
    const item = readInlineData(itemFragment, itemRef);
    const locale = serverVars.get('locale');
    const name = locale === 'en-US' ? item?.title : 'n/a';
    const {
        seller,
        pricing,
        browseUrl,
        serviceId,
        categoryCode,
        isAuctionEnabled,
        relevantAuctionLot,
        relevantAuctionSolution,
        contemporaryTrackingString,
    } = item || {};

    let price = pricing?.totalAmount?.convertedAmounts?.USD;
    let dimension76 = getPdpTrackingVpvParamValue(itemRef?.pdpTrackingVpvParams, 'priceType');

    if (isAuctionEnabled) {
        const { bids, highestBidAmount } = relevantAuctionSolution || {};
        const hasBids = !!bids?.totalResults;

        price = hasBids
            ? highestBidAmount?.convertedAmounts?.USD
            : relevantAuctionLot?.buyItNowPrice?.convertedAmounts?.USD;
        dimension76 = `auction ${!hasBids ? 'bin ' : ''}price`;
    }

    trackEcommerce({
        type: 'addToCart',
        eventName,
        products: [
            {
                brand: seller?.serviceId,
                category: browseUrl,
                categoryCode: categoryCode,
                dimension76,
                dimension83: contemporaryTrackingString,
                id: serviceId,
                name,
                price,
            },
        ],
    });
}

export function trackMakeOfferFromEmail(template: string, item?: Maybe<PdpTrackingVpvProps>): void {
    trackEvent({
        category: 'make offer click',
        action: `email make offer button - ${(template || '').toLowerCase()}`,
        eventName: EVENT_PRODUCT_INTERACTION,
        label: getPurchaseStatus(item),
        interaction_type: INTERACTION_TYPE_MAKE_OFFER,
        step_interaction_name: STEP_INTERACTION_EMAIL_MAKE_OFFER,
        isInteractiveEvent: true,
    });
}

export function trackMakeOfferClick(
    item: Maybe<Item>,
    hasPrivateOffer: Maybe<boolean> = false,
    event: TrackableEvent = null,
    location?: Location
): void {
    let trackObj;

    if (hasPrivateOffer && !location) {
        trackObj = {
            action: 'offer pdp counter offer',
            category: 'counter offer click',
            label: getPurchaseStatus(item),
            eventName: EVENT_PRODUCT_INTERACTION,
            interaction_type: INTERACTION_TYPE_COUNTER_OFFER,
            step_interaction_name: STEP_INTERACTION_COUNTER_OFFER,
        };
    } else {
        trackObj = {
            category: 'make offer click',
            action:
                location && !!makeAnOfferLocation[location]
                    ? makeAnOfferLocation[location]
                    : 'pdp make offer button',
            eventName: EVENT_PRODUCT_INTERACTION,
            label:
                location && !!makeAnOfferLocation[location]
                    ? makeAnOfferLocation[location]
                    : getPurchaseStatus(item),
            interaction_type: INTERACTION_TYPE_MAKE_OFFER,
            step_interaction_name: STEP_INTERACTION_MAKE_OFFER,
            additional: { currentOfferType: `${hasPrivateOffer ? 'private' : 'regular'} offer` },
        };
    }

    trackEvent(trackObj, event);
    fireTrackEcommerce(item, EVENT_ADD_CART);
}

export function trackPurchaseClick(
    item: Maybe<Item> = null,
    event: TrackableEvent = null,
    location?: Location,
    hasPrivateOffer: Maybe<boolean> = false
): void {
    const itemData = readInlineData(itemFragment, item);
    const { serviceId, isAuctionEnabled, relevantAuctionSolution, lastTransaction } =
        itemData || {};
    const { bids, highestBidAmount } = relevantAuctionSolution || {};
    const hasBids = bids?.totalResults;
    const secondChanceOffer = lastTransaction?.secondChanceOffer;
    if (isAuctionEnabled && (hasBids || secondChanceOffer)) {
        // only fire custom event for auctions with bids or 2nd chance offer
        if (secondChanceOffer) {
            trackEvent(
                {
                    category: 'auction',
                    action: 'auction pdp - accept offer entry',
                    label: 'accept offer button click',
                    value: secondChanceOffer?.amountPerItem?.convertedAmounts?.USD || 0,
                    additional: {
                        itemId: serviceId,
                    },
                },
                event
            );
        } else {
            trackEvent(
                {
                    category: 'auction',
                    action: 'auction pdp - complete purchase entry',
                    label: 'complete purchase button clicked',
                    value: highestBidAmount?.convertedAmounts?.USD || 0,
                    additional: {
                        itemId: serviceId,
                    },
                },
                event
            );
        }
    } else if (location && !!purchaseLocation[location]) {
        trackEvent(
            {
                category: 'purchase click',
                action: purchaseLocation[location],
                eventName: EVENT_PRODUCT_INTERACTION,
                label: purchaseLocation[location],
                interaction_type: INTERACTION_TYPE_ADD_TO_CART,
                step_interaction_name: STEP_INTERACTION_ADD_TO_CART,
            },
            event
        );
    } else if (hasPrivateOffer) {
        trackEvent(
            {
                category: 'accept offer click',
                action: 'offer pdp accept offer',
                label: getPurchaseStatus(item),
                eventName: EVENT_PRODUCT_INTERACTION,
                interaction_type: INTERACTION_TYPE_ACCEPT_OFFER,
                step_interaction_name: STEP_INTERACTION_ACCEPT_OFFER,
            },
            event
        );
    } else {
        trackEvent(
            {
                category: 'purchase click',
                action: 'pdp purchase button',
                eventName: EVENT_PRODUCT_INTERACTION,
                label: getPurchaseStatus(item),
                interaction_type: INTERACTION_TYPE_ADD_TO_CART,
                step_interaction_name: STEP_INTERACTION_ADD_TO_CART,
            },
            event
        );
    }
    fireTrackEcommerce(item, EVENT_ADD_CART);
}
